import React, { useState, useEffect } from 'react';
import app, { auth, db, storage } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, getDoc, updateDoc, setDoc, addDoc, deleteDoc, serverTimestamp, collection, getDocs, query, where, deleteField } from 'firebase/firestore';
import { loadLinkedAthletes } from '../utils/athleteLinks';
import { cleanNames } from '../utils/names';
import { resolveMember } from '../utils/resolveMember';
import { resizeImage } from '../utils/resizeImage';
import { sendPasswordResetEmail } from 'firebase/auth';
import Layout from '../components/Layout';
import { logError } from '../utils/errorLogger';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Button, TextField, MenuItem,
  Alert, CircularProgress, Avatar, Chip, Tabs, Tab, Divider,
  Checkbox, FormControlLabel, RadioGroup, Radio, FormLabel, FormControl, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import Tooltip from '@mui/material/Tooltip';
import EditIcon from '@mui/icons-material/Edit';
import LockResetIcon from '@mui/icons-material/LockReset';
import { getRoleChipsConfig } from '../utils/memberChips';
import MyGateAccess from '../components/MyGateAccess';

const REFERRAL_SOURCES = ['Web Search', 'Social Media', 'Friend', 'Coach', 'Existing Member'];
const RELATIONSHIPS = ['Mother', 'Father', 'Guardian', 'Grandparent', 'Other'];

const ROLE_COLORS = {
  admin: { bg: '#FDEDEC', color: '#922B21' },
  coach: { bg: '#EBF5FB', color: '#1A5276' },
  assistant_coach: { bg: '#EBF5FB', color: '#1A5276' },
  volunteer: { bg: '#F4F6F7', color: '#555' },
  parent: { bg: '#E9F7EF', color: '#1E6B3C' },
  adult_athlete: { bg: '#FEF9E7', color: '#7B4800' },
  junior_athlete: { bg: '#F5EEF8', color: '#6C3483' },
  member: { bg: '#FEF5D7', color: '#7E6010' },
  renter: { bg: '#E0F2F1', color: '#00695C' },
  lead: { bg: '#FDF2E9', color: '#A04000' },
  player: { bg: '#EBF5FB', color: '#21618C' },
};
const ROLE_LABELS = {
  admin: 'Admin', coach: 'Coach', assistant_coach: 'Assistant Coach',
  volunteer: 'Volunteer', parent: 'Parent', adult_athlete: 'Adult Athlete',
  junior_athlete: 'Junior Athlete', member: 'Member', renter: 'Renter', lead: 'Lead', player: 'Player',
};

// Tier-name → visual style for membership badge near the user's name. The match
// is a case-insensitive "contains" check against the tier name from
// users.membership.tierName, so a tier named "Gold Membership - Annual" still
// matches the GOLD style.

// Returns the matching tag style for a given tierName, or null.

const InfoRow = ({ label, value }) => (
  <Grid size={{ xs: 12, sm: 6 }}>
    <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>{label}</Typography>
    <Typography variant="body1" sx={{ fontWeight: 500 }}>{value || '—'}</Typography>
  </Grid>
);

const SecTitle = ({ title }) => (
  <Box sx={{ mt: 3, mb: 2 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F' }}>{title}</Typography>
    <Divider sx={{ mt: 0.5 }} />
  </Box>
);

function TabPanel({ value, index, children }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

const beachToSkillGroup = (beachExp) => {
  if (!beachExp) return '';
  const b = beachExp.toLowerCase();
  if (b.includes('no experience') || b.includes('beginner')) return 'Beginner';
  if (b.includes('intermediate')) return 'Intermediate';
  if (b.includes('advanced')) return 'Advanced';
  return '';
};

function Profile() {
  const [userData, setUserData] = useState(null);
  const [athleteData, setAthleteData] = useState(null);
  const [linkedAthletes, setLinkedAthletes] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [memberList, setMemberList] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isContractor, setIsContractor] = useState(false);
  const [workEntries, setWorkEntries] = useState([]);
  const [workForm, setWorkForm] = useState({ date: (() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}`; })(), description: '', hours: '', rate: '' });
  const [workEditId, setWorkEditId] = useState(null);
  const [workSaving, setWorkSaving] = useState(false);
  const [workMsg, setWorkMsg] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = React.useRef();
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [tab, setTab] = useState(0);
  const [mmBusy, setMmBusy] = useState(false);
  // Outstanding court-time invoices. A renter could not see these at all: the
  // monthly job wrote a row, nothing was emailed, and the rules kept invoices to
  // admins — so the bill existed only in the club's records while unpaid court
  // time now blocks new bookings.
  const [courtInvoices, setCourtInvoices] = useState([]);
  const [payingInvoice, setPayingInvoice] = useState('');

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) { setCourtInvoices([]); return; }
    getDocs(query(collection(db, 'customerInvoices'), where('memberId', '==', uid)))
      .then(snap => setCourtInvoices(snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(i => i.paid !== true && (Number(i.amount) || 0) > 0)
        .sort((a, b) => String(a.invoiceDate || '').localeCompare(String(b.invoiceDate || '')))))
      // Nothing to show is the normal case, and a rules miss is not worth an
      // error on a page that is mostly about something else.
      .catch(() => setCourtInvoices([]));
  }, []);
  const [mmMsg, setMmMsg] = useState(null);
  const [freezeDialog, setFreezeDialog] = useState(false);
  const [freezeMonths, setFreezeMonths] = useState(1);
  const [cancelDialog, setCancelDialog] = useState(false);
  const [hasP2, setHasP2] = useState(false);
  const [form, setForm] = useState({
    firstName: '', lastName: '', nickname: '', phone: '',
    address: '', city: '', state: '', zip: '',
    relationship: '', referredBy: '', referredByMemberId: '', referredByStaffId: '',
    guardian2FirstName: '', guardian2LastName: '', guardian2Email: '', guardian2Phone: '',
  });
  const [athleteForm, setAthleteForm] = useState({});
  const [juniorForms, setJuniorForms] = useState({});
  const user = auth.currentUser;
  const navigate = useNavigate();
  const [memberUid, setMemberUid] = useState(user?.uid || null);
  const [memberEmail, setMemberEmail] = useState(user?.email || '');

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
      // Resolve to the canonical member: if this login was linked into another
      // account, load that one so the member sees their real data regardless of
      // which linked email they used to sign in.
      const member = await resolveMember(db, user);
      const memberUidLocal = member.uid;
      const memberEmailLocal = member.email;
      setMemberUid(memberUidLocal);
      setMemberEmail(memberEmailLocal);
      const userDoc = await getDoc(doc(db, 'users', memberUidLocal));
	  const roles = userDoc.exists() ? (userDoc.data().roles || []) : [];
	  const isAdmin = roles.includes('admin');

		if (isAdmin) {
		const [usersSnap, coachSnap] = await Promise.all([
		getDocs(collection(db, 'users')),
		getDocs(query(collection(db, 'users'), where('roles', 'array-contains-any', ['coach', 'assistant_coach']))),
  ]);
  const allUsers = usersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
  setMemberList(allUsers.filter(u => !(u.roles || []).some(r => ['coach', 'assistant_coach', 'volunteer'].includes(r))));
  setStaffList([
    ...allUsers.filter(u => (u.roles || []).some(r => ['coach', 'assistant_coach', 'volunteer'].includes(r))),
    ...coachSnap.docs.map(d => ({ id: d.id, ...d.data() })),
  ]);
}
        if (userDoc.exists()) {
        const d = userDoc.data();
        setUserData(d);
        const profileRoles = d.roles || [];
        setIsContractor(profileRoles.includes('contractor'));
        if (profileRoles.includes('contractor')) {
          try {
            const weSnap = await getDocs(query(collection(db, 'workEntries'), where('workerId', '==', memberUid || user.uid)));
            setWorkEntries(weSnap.docs.map(w => ({ id: w.id, ...w.data() })).sort((a, b) => (b.date || '').localeCompare(a.date || '')));
          } catch (e) { console.error('workEntries load', e); }
        }
        setForm({
          firstName: d.firstName || '', lastName: d.lastName || '',
          nickname: d.nickname || '', phone: d.phone || '',
          address: d.address || '', city: d.city || '',
          state: d.state || '', zip: d.zip || '',
          relationship: d.relationship || '',
          referredBy: d.referredBy ? d.referredBy.split(' - ')[0] : '',
          referredByFull: d.referredBy || '',
          referredByMemberId: d.referredByMemberId || '',
          referredByStaffId: d.referredByStaffId || '',
          guardian2FirstName: d.guardian2FirstName || d.p2FirstName || '',
          guardian2LastName: d.guardian2LastName || d.p2LastName || '',
          guardian2Email: d.guardian2Email || d.p2Email || '',
          guardian2Phone: d.guardian2Phone || d.p2Phone || '',
        });
        if (d.guardian2FirstName || d.guardian2Email || d.p2FirstName || d.p2Email) setHasP2(true);
      }
      const athleteSnap = await getDocs(query(collection(db, 'athletes'), where('email', '==', memberEmailLocal)));
      const ownAthlete = athleteSnap.docs.find(d => d.data().athleteType?.toLowerCase() === 'adult');
      let ownAthleteData = null;
      if (ownAthlete) {
        ownAthleteData = { id: ownAthlete.id, ...ownAthlete.data() };
        setAthleteData(ownAthleteData);
        const ad = ownAthlete.data();
        setAthleteForm({ skillGroup: ad.skillGroup || '', shirtSize: ad.shirtSize || '', indoorExperience: ad.indoorExperience || '', beachExperience: ad.beachExperience || '' });
      }
      const juniors = await loadLinkedAthletes(db, memberUidLocal);
      setLinkedAthletes(juniors);
      const jf = {};
      juniors.forEach(j => { jf[j.id] = { firstName: j.firstName || '', lastName: j.lastName || '', email: j.email || '', phone: j.phone || '', nickname: j.nickname || '', gender: j.gender || '', shirtSize: j.shirtSize || '', skillGroup: j.skillGroup || '', beachExperience: j.beachExperience || '', indoorExperience: j.indoorExperience || '' }; });
      setJuniorForms(jf);
      // Fetch purchases keyed to this user. Some purchases are tied to an athlete
      // record (session plans) and use `athleteId`; others (memberships) are tied
      // directly to the user and use `userId`. We query both and merge.
      const purchaseQueries = [];
      if (ownAthleteData) {
        purchaseQueries.push(getDocs(query(collection(db, 'purchases'), where('athleteId', '==', ownAthleteData.id))));
      }
      purchaseQueries.push(getDocs(query(collection(db, 'purchases'), where('userId', '==', memberUidLocal))));
      const purSnaps = await Promise.all(purchaseQueries);
      const purMap = new Map();
      purSnaps.forEach(snap => {
        snap.docs.forEach(d => {
          if (!purMap.has(d.id)) purMap.set(d.id, { id: d.id, ...d.data() });
        });
      });
      setPurchases([...purMap.values()].sort((a, b) =>
        (b.purchaseDate?.toDate?.() || 0) - (a.purchaseDate?.toDate?.() || 0)
      ));

      if (ownAthleteData) {
        const attSnap = await getDocs(query(collection(db, 'attendance'), where('athleteId', '==', ownAthleteData.id)));
        setAttendance(attSnap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (b.date || '').localeCompare(a.date || '')));
      }
      // Load tournament registrations by email match (covers self + partners using same email)
      const userEmail = (userDoc.data().email || memberEmailLocal || '').toLowerCase();
      if (userEmail) {
        try {
          const regSnap = await getDocs(query(collection(db, 'registrations'), where('email', '==', userEmail)));
          const regs = regSnap.docs.map(d => ({ id: d.id, ...d.data() }));
          // Resolve tournament names in a separate pass
          const tournIds = [...new Set(regs.map(r => r.tournament_id).filter(Boolean))];
          const tournMap = {};
          await Promise.all(tournIds.map(async tid => {
            try {
              const tdoc = await getDoc(doc(db, 'tournaments', tid));
              if (tdoc.exists()) tournMap[tid] = tdoc.data();
            } catch (e) { /* ignore */ }
          }));
          const enriched = regs.map(r => ({
            ...r,
            tournamentName: tournMap[r.tournament_id]?.name || tournMap[r.tournament_id]?.tournament_name || 'Tournament',
            tournamentDate: tournMap[r.tournament_id]?.date || tournMap[r.tournament_id]?.start_date || null,
          })).sort((a, b) => {
            // Newest first by created_at or tournament date
            const aTime = a.created_at?.toDate?.() || (a.tournamentDate ? new Date(a.tournamentDate) : 0);
            const bTime = b.created_at?.toDate?.() || (b.tournamentDate ? new Date(b.tournamentDate) : 0);
            return bTime - aTime;
          });
          setRegistrations(enriched);
        } catch (e) {
          console.error('Failed to load registrations:', e);
        }
      }
      setLoading(false);
    };
    fetchData();
  }, [user]);

  const update = (f, v) => setForm(p => ({ ...p, [f]: v }));
  const updateJunior = (id, f, v) => setJuniorForms(p => ({ ...p, [id]: { ...p[id], [f]: v } }));

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const resized = await resizeImage(file, { maxDim: 1024, targetBytes: 200 * 1024 });
      const storageRef = ref(storage, `profile_photos/users/${user.uid}_${Date.now()}`);
      await uploadBytes(storageRef, resized, { contentType: resized.type || 'image/jpeg' });
      const url = await getDownloadURL(storageRef);
      await setDoc(doc(db, 'users', memberUid || user.uid), { photoURL: url }, { merge: true });
      // Also mirror onto adult athlete doc if linked, so People list / AthleteProfile see the same photo
      if (athleteData?.id) {
        try { await updateDoc(doc(db, 'athletes', athleteData.id), { photoURL: url }); } catch (e) { /* ignore */ }
        setAthleteData(prev => prev ? ({ ...prev, photoURL: url }) : prev);
      }
      setUserData(prev => ({ ...prev, photoURL: url }));
    } catch (err) {
      console.error('Photo upload failed:', err);
      logError('Profile', err.message || String(err), err.stack || '');
      setError('Photo upload failed: ' + err.message);
    }
    setUploadingPhoto(false);
  };

  const handleManageSubscription = async () => {
    if (!athleteData?.id) return;
    try {
      const functions = getFunctions(app, 'us-central1');
      const portalSession = httpsCallable(functions, 'createBillingPortalSession');
      const result = await portalSession({
        athleteId: athleteData.id,
        returnUrl: window.location.href,
      });
      const url = result.data?.url;
      if (url) {
        window.location.href = url;
      } else {
        setError('Portal session created but no URL returned.');
      }
    } catch (err) {
      setError(`Couldn't open billing portal: ${err.message || err.code || 'Unknown error'}`);
    }
  };

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      try {
        // Normalize guardian2 email to lowercase for Phase B matching.
        // Also delete any legacy p2* fields so the doc stays clean.
        const normalized = cleanNames({
          ...form,
          guardian2Email: (form.guardian2Email || '').toLowerCase().trim(),
        });
        const updates = {
          ...normalized,
          p2FirstName: deleteField(),
          p2LastName: deleteField(),
          p2Email: deleteField(),
          p2Phone: deleteField(),
          p2Nickname: deleteField(),
          p2Address: deleteField(),
          p2City: deleteField(),
          p2State: deleteField(),
          p2Zip: deleteField(),
          p2Relationship: deleteField(),
          updatedAt: new Date(),
        };
        await setDoc(doc(db, 'users', memberUid || user.uid), updates, { merge: true });
        setUserData(prev => ({ ...prev, ...normalized }));
      } catch (err) {
        console.error('Failed saving users doc:', err.code, err.message);
      logError('Profile', err.message || String(err), err.stack || '');
        setError('Failed to save profile: ' + (err.message || err.code));
        setSaving(false); return;
      }
      if (athleteData?.id) {
        try {
          console.log('Updating athlete doc:', athleteData.id, 'athleteForm:', athleteForm);
          await updateDoc(doc(db, 'athletes', athleteData.id), { ...cleanNames(athleteForm), updatedAt: new Date() });
          setAthleteData(prev => ({ ...prev, ...athleteForm }));
        } catch (err) {
          console.error('Failed saving athlete doc:', err.code, err.message);
      logError('Profile', err.message || String(err), err.stack || '');
          setError('Failed to save athlete info: ' + (err.message || err.code));
          setSaving(false); return;
        }
      }
      if (Object.keys(juniorForms).length > 0) {
        for (const [id, jf] of Object.entries(juniorForms)) {
		console.log('Current user UID:', user.uid);
		console.log('Saving junior athlete ID:', id, 'data:', jf);	
          try {
            await updateDoc(doc(db, 'athletes', id), { ...cleanNames(jf), skillGroup: beachToSkillGroup(jf.beachExperience), updatedAt: new Date() });
          } catch (err) {
            console.error('Failed saving junior athlete', id, err.code, err.message);
      logError('Profile', err.message || String(err), err.stack || '');
            setError('Failed to save junior athlete: ' + (err.message || err.code));
            setSaving(false); return;
          }
        }
        setLinkedAthletes(prev => prev.map(j => ({ ...j, ...juniorForms[j.id] })));
      }
      setEditing(false);
      setSuccess('Profile updated successfully.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Profile save error:', err.code, err.message);
      logError('Profile', err.message || String(err), err.stack || '');
      setError('Failed to save: ' + (err.message || err.code || 'Unknown error'));
    }
    setSaving(false);
  };

  const handlePasswordReset = async () => {
    try {
      await sendPasswordResetEmail(auth, user.email);
      setSuccess('Password reset email sent to ' + user.email);
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) { setError('Failed to send password reset email.'); }
  };

  const formatDate = (s) => {
    if (!s) return '—';
    const [y, m, d] = s.split('-');
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };
  const formatTs = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const memberSince = userData?.memberSince || userData?.createdAt || userData?.registrationDate;

  if (loading) return <Layout><Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box></Layout>;

  const initials = (form.firstName?.charAt(0) || '') + (form.lastName?.charAt(0) || '');

  // Which optional tabs are present, and therefore what index each later tab
  // sits at. These once excluded coaches, which was wrong twice over: a coach
  // can also be a paying member with sessions and purchases of their own, and
  // the Sessions PANEL below is hardcoded to index 2 with no such exclusion.
  // A coach with an athlete record was landing on Registrations at index 2 and
  // getting the Sessions panel rendered underneath it, because the tab list and
  // the panel list disagreed about who existed.
  const showPurchasesTab = !!(athleteData || purchases.length > 0);

  // Compute the index of the Registrations tab based on which optional tabs are present
  let registrationsTabIndex = 1; // Contact Info is always index 0
  if (athleteData) registrationsTabIndex++;             // Athlete Details
  if (athleteData) registrationsTabIndex++;             // Sessions
  if (showPurchasesTab) registrationsTabIndex++;        // Purchases
  // A membership is a membership however it was granted. Requiring a Stripe
  // subscription id here hid this tab from every admin-assigned membership —
  // ambassadors, comped accounts, day passes, prepaid annuals — 17 active
  // members at the time this was written, who could see no record anywhere in
  // the app of the thing they hold. The Stripe id still decides which CONTROLS
  // appear inside the tab, below; it has no business deciding whether someone
  // is shown their own membership.
  const hasMembership = !!userData?.membership?.tierId;
  const membershipTabIndex = registrationsTabIndex + 1;
  const myWorkTabIndex = registrationsTabIndex + 1 + (hasMembership ? 1 : 0);
  const payCourtInvoice = async (inv) => {
    setPayingInvoice(inv.id);
    try {
      const fn = httpsCallable(getFunctions(), 'createCourtInvoiceCheckout');
      const { data } = await fn({ invoiceId: inv.id });
      if (data?.url) window.location.href = data.url;
      else setMmMsg({ severity: 'error', text: 'Could not start the payment.' });
    } catch (e) {
      setMmMsg({ severity: 'error', text: e.message || 'Could not start the payment.' });
    }
    setPayingInvoice('');
  };

  const manageMembership = async (action, months) => {
    setMmBusy(true); setMmMsg(null);
    try {
      const functions = getFunctions(app, 'us-central1');
      const fn = httpsCallable(functions, 'manageMembership');
      const r = await fn({ action, months });
      setFreezeDialog(false); setCancelDialog(false);
      const texts = {
        cancel: `Your membership will end on ${r.data.effectiveDate ? new Date(r.data.effectiveDate).toLocaleDateString() : 'your next billing date'}. You keep full access until then.`,
        uncancel: 'Cancellation removed \u2014 your membership continues as normal.',
        freeze: `Freeze scheduled: ${new Date(r.data.startsAt).toLocaleDateString()} \u2192 ${new Date(r.data.endsAt).toLocaleDateString()}. Billing and access pause during the freeze and resume automatically.`,
        unfreeze: 'Freeze removed \u2014 your membership continues as normal.',
      };
      setMmMsg({ severity: 'success', text: texts[action] || 'Done.' });
      // Refresh membership state from Firestore
      const snap = await getDoc(doc(db, 'users', user.uid));
      if (snap.exists()) setUserData(prev => ({ ...prev, membership: snap.data().membership }));
    } catch (err) {
      setMmMsg({ severity: 'error', text: err.message || 'Membership update failed.' });
    } finally { setMmBusy(false); }
  };

  const reloadWork = async () => {
    const weSnap = await getDocs(query(collection(db, 'workEntries'), where('workerId', '==', memberUid || user.uid)));
    setWorkEntries(weSnap.docs.map(w => ({ id: w.id, ...w.data() })).sort((a, b) => (b.date || '').localeCompare(a.date || '')));
  };
  const submitWork = async () => {
    setWorkMsg('');
    if (!workForm.description.trim()) { setWorkMsg('err:Please describe the work performed.'); return; }
    const hrs = workForm.hours === '' ? null : parseFloat(workForm.hours);
    if (workForm.hours !== '' && (isNaN(hrs) || hrs <= 0)) { setWorkMsg('err:Hours must be a positive number.'); return; }
    setWorkSaving(true);
    try {
      const payType = userData?.payType || 'per_job';
      const myRate = parseFloat(userData?.hourlyRate);
      let entryRate = null, entryAmount = null, entryApproved = false;
      if (payType === 'hourly') {
        if (!hrs) { setWorkMsg('err:Hours are required for hourly work.'); setWorkSaving(false); return; }
        if (isNaN(myRate) || myRate <= 0) { setWorkMsg('err:No hourly rate on file yet — contact the admin.'); setWorkSaving(false); return; }
        entryRate = myRate; entryAmount = Math.round(hrs * myRate * 100) / 100; entryApproved = true;
      } else if (payType === 'self_rate') {
        const selfRate = parseFloat(workForm.rate);
        if (!hrs || isNaN(selfRate) || selfRate <= 0) { setWorkMsg('err:Hours and rate are both required.'); setWorkSaving(false); return; }
        entryRate = selfRate; entryAmount = Math.round(hrs * selfRate * 100) / 100; entryApproved = false;
      }
      const common = {
        date: workForm.date, description: workForm.description.trim(),
        hours: hrs, type: hrs ? 'hours' : 'task',
        rate: entryRate, amount: entryAmount, approved: entryApproved,
      };
      if (workEditId) {
        await updateDoc(doc(db, 'workEntries', workEditId), common);
      } else {
        await addDoc(collection(db, 'workEntries'), {
          ...common, workerId: memberUid || user.uid,
          workerName: `${userData?.firstName || ''} ${userData?.lastName || ''}`.trim() || user.email,
          paid: false, paidAt: null, submittedByWorker: true,
          createdBy: user.uid, createdAt: serverTimestamp(),
        });
      }
      setWorkForm({ date: (() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}`; })(), description: '', hours: '', rate: '' });
      setWorkEditId(null);
      setWorkMsg('ok:' + (workEditId ? 'Entry updated.' : 'Work entry submitted — pending admin review.'));
      await reloadWork();
    } catch (e) { console.error(e); setWorkMsg('err:Failed to save. Please try again.'); }
    setWorkSaving(false);
  };
  const deleteWork = async (id) => {
    if (!window.confirm('Delete this work entry?')) return;
    try { await deleteDoc(doc(db, 'workEntries', id)); await reloadWork(); }
    catch (e) { console.error(e); setWorkMsg('err:Could not delete entry.'); }
  };

  let purchasesTabIndex = null;
  if (showPurchasesTab) {
    purchasesTabIndex = 1;
    if (athleteData) purchasesTabIndex++;             // Athlete Details
    if (athleteData) purchasesTabIndex++;             // Sessions
  }

  return (
    <Layout>
      <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 860, mx: 'auto' }}>

        {/* Header */}
        <Paper elevation={2} sx={{ p: 3, borderRadius: 3, mb: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: 3 }}>
          <Tooltip title="Click to change photo">
            <Box sx={{ position: 'relative', cursor: 'pointer', flexShrink: 0 }}
              onClick={() => photoInputRef.current.click()}>
              <Avatar
                src={userData?.photoURL || athleteData?.photoURL || ''}
                sx={{ width: 72, height: 72, backgroundColor: '#1E3A5F', fontSize: '1.6rem' }}>
                {!(userData?.photoURL || athleteData?.photoURL) && (initials || user?.email?.charAt(0).toUpperCase())}
              </Avatar>
              <Box sx={{
                position: 'absolute', inset: 0, borderRadius: '50%',
                backgroundColor: 'rgba(0,0,0,0.45)', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                opacity: uploadingPhoto ? 1 : 0,
                '&:hover': { opacity: 1 }, transition: 'opacity 0.2s',
              }}>
                {uploadingPhoto
                  ? <CircularProgress size={20} sx={{ color: 'white' }} />
                  : <PhotoCameraIcon sx={{ color: 'white', fontSize: 22 }} />
                }
              </Box>
              <input ref={photoInputRef} type="file" accept="image/*"
                style={{ display: 'none' }}
                onChange={handlePhotoUpload} />
            </Box>
          </Tooltip>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1E3A5F' }}>{form.firstName} {form.lastName}</Typography>
            <Typography variant="body2" sx={{ color: '#888' }}>{user?.email}</Typography>
            {userData?.roles && (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                {(() => {
                  // Roles with the generic 'member' chip dropped whenever a tier
                  // badge takes its place. getRoleChipsConfig is the one copy of
                  // that rule, and the one place a court renter is excluded.
                  const { roles: visibleRoles } = getRoleChipsConfig(userData);
                  return visibleRoles.map(role => (
                    <Chip key={role} label={ROLE_LABELS[role] || role} size="small"
                      sx={{ backgroundColor: ROLE_COLORS[role]?.bg || '#f0f0f0', color: ROLE_COLORS[role]?.color || '#555', fontWeight: 600, fontSize: '0.75rem' }} />
                  ));
                })()}
                {/* Membership tier tag — shown when the member is in good standing
                    (active or trialing) AND the tier matches a known style.
                    Never for a court renter, whatever their tier is called. */}
                {(() => {
                  const { tierStyle: style } = getRoleChipsConfig(userData);
                  if (!style) return null;
                  return (
                    <Chip
                      label={style.label}
                      size="small"
                      sx={{
                        backgroundColor: style.bg,
                        color: style.color,
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        letterSpacing: '0.5px',
                      }}
                    />
                  );
                })()}
              </Box>
            )}
          </Box>
          {/* Right side: session balance, subscription, edit button */}
          <Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', sm: 'flex-end' }, gap: 1.5, minWidth: { sm: 200 } }}>
            {/* Top row: balance + edit button side by side */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              {athleteData && (
                <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', lineHeight: 1, color: (athleteData.sessionBalance ?? 0) > 2 ? '#27AE60' : (athleteData.sessionBalance ?? 0) > 0 ? '#E67E22' : '#E74C3C' }}>
                    {athleteData.sessionBalance ?? 0}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#888' }}>sessions remaining</Typography>
                </Box>
              )}
              {!editing && (
                <Button startIcon={<EditIcon />} onClick={() => setEditing(true)} variant="outlined" size="small" sx={{ borderColor: '#1E3A5F', color: '#1E3A5F' }}>Edit</Button>
              )}
            </Box>

            {/* Subscription row — full width, separated by a divider */}
            {athleteData && athleteData.subscriptionStatus && (() => {
              const subStatus = athleteData.subscriptionStatus;
              const subActive = subStatus === 'active' || subStatus === 'trialing';
              const cancelAtPeriodEnd = athleteData.subscriptionCancelAtPeriodEnd === true;
              // Convert Firestore Timestamp or Date to a readable label
              let cancelDateLabel = '';
              if (cancelAtPeriodEnd && athleteData.subscriptionCancelAt) {
                const ts = athleteData.subscriptionCancelAt;
                const d = ts.toDate ? ts.toDate() : new Date(ts);
                if (!isNaN(d)) {
                  cancelDateLabel = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                }
              }
              const colors = cancelAtPeriodEnd ? { label: cancelDateLabel ? `Cancels ${cancelDateLabel}` : 'Canceling', color: '#E67E22' }
                : subStatus === 'active' ? { label: 'Active', color: '#27AE60' }
                : subStatus === 'past_due' ? { label: 'Past Due', color: '#E67E22' }
                : subStatus === 'canceled' ? { label: 'Canceled', color: '#888' }
                : subStatus === 'trialing' ? { label: 'Trial', color: '#3498DB' }
                : { label: subStatus, color: '#888' };
              return (
                <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', sm: 'flex-end' }, gap: 1, pt: 1, borderTop: '1px solid #eee' }}>
                  <Typography variant="caption" sx={{ color: '#888' }}>Subscription</Typography>
                  <Chip label={colors.label} size="small"
                        sx={{ backgroundColor: colors.color, color: 'white', fontWeight: 600, fontSize: '0.7rem' }} />
                  {subActive && athleteData.stripeSubscriptionId && (
                    <Button size="small" variant="outlined"
                            onClick={() => navigate('/billing')}
                            sx={{ borderColor: '#1E3A5F', color: '#1E3A5F', fontSize: '0.7rem', py: 0.25, ml: 0.5 }}>
                      Manage Billing
                    </Button>
                  )}
                </Box>
              );
            })()}
          </Box>
        </Paper>

        <MyGateAccess />

        {/* Linked Athletes */}
        {linkedAthletes.length > 0 && (
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1E3A5F', mb: 2 }}>Linked Athletes</Typography>
            {linkedAthletes.map(athlete => (
              <Box key={athlete.id} onClick={() => navigate(`/athletes/${athlete.id}`)}
                sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, p: 1.5, borderRadius: 2, backgroundColor: '#f9f9f9', cursor: 'pointer', '&:hover': { backgroundColor: '#DEEAF1' } }}>
                <Avatar sx={{ width: 40, height: 40, backgroundColor: '#2E6DA4', fontSize: '0.9rem' }}>
                  {(athlete.firstName?.charAt(0) || '') + (athlete.lastName?.charAt(0) || '')}
                </Avatar>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{athlete.firstName} {athlete.lastName}</Typography>
                  <Typography variant="body2" sx={{ color: '#888', textTransform: 'capitalize' }}>{athlete.athleteType} · {athlete.skillGroup || 'No skill group'}</Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: (athlete.sessionBalance || 0) > 2 ? '#27AE60' : (athlete.sessionBalance || 0) > 0 ? '#E67E22' : '#E74C3C' }}>
                  {athlete.sessionBalance || 0} sessions
                </Typography>
              </Box>
            ))}
          </Paper>
        )}

        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Tabs */}
        <Paper elevation={2} sx={{ borderRadius: 3 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: '1px solid #eee', px: 2 }} variant="scrollable" scrollButtons="auto">
            <Tab label="Contact Info" />
            {athleteData && <Tab label="Athlete Details" />}
            {athleteData && <Tab label="Sessions" />}
            {showPurchasesTab && <Tab label="Purchases" />}
            <Tab label="Registrations" />
            {hasMembership && <Tab label="Membership" />}
            {isContractor && <Tab label="My Work" />}
          </Tabs>

          <Box sx={{ p: 3 }}>

            {/* ── TAB 0: Contact Info ── */}
            <TabPanel value={tab} index={0}>

              {/* Parent / Guardian 1 */}
              <SecTitle title="Parent / Guardian 1" />
              <Grid container spacing={2} sx={{ mb: 2 }}>
                {editing ? (
                  <>
                    <Grid size={{ xs: 12, sm: 6 }}><TextField required fullWidth label="First Name *" value={form.firstName} onChange={e => update('firstName', e.target.value)} /></Grid>
                    <Grid size={{ xs: 12, sm: 6 }}><TextField required fullWidth label="Last Name *" value={form.lastName} onChange={e => update('lastName', e.target.value)} /></Grid>
                    <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth label="Email (login)" value={user?.email || ''} disabled helperText="Login email cannot be changed here" /></Grid>
                    <Grid size={{ xs: 12, sm: 6 }}><TextField required fullWidth label="Phone *" value={form.phone} onChange={e => update('phone', e.target.value)} /></Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField required fullWidth select label="Relationship *" value={form.relationship} onChange={e => update('relationship', e.target.value)}>
                        {RELATIONSHIPS.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth label="Nickname" value={form.nickname} onChange={e => update('nickname', e.target.value)} /></Grid>
                    <Grid size={{ xs: 12 }}><TextField fullWidth label="Street Address" value={form.address} onChange={e => update('address', e.target.value)} /></Grid>
                    <Grid size={{ xs: 12, sm: 5 }}><TextField fullWidth label="City" value={form.city} onChange={e => update('city', e.target.value)} /></Grid>
                    <Grid size={{ xs: 12, sm: 3 }}><TextField fullWidth label="State" value={form.state} onChange={e => update('state', e.target.value)} inputProps={{ maxLength: 2 }} /></Grid>
                    <Grid size={{ xs: 12, sm: 4 }}><TextField fullWidth label="ZIP" value={form.zip} onChange={e => update('zip', e.target.value)} /></Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField fullWidth select label="Referred By" value={form.referredBy} onChange={e => { update('referredBy', e.target.value); update('referredByMemberId', ''); update('referredByStaffId', ''); }}>
                        <MenuItem value="">— Select —</MenuItem>
                        {REFERRAL_SOURCES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                      </TextField>
                    </Grid>
                    {form.referredBy === 'Existing Member' && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField fullWidth select label="Which Member?" value={form.referredByMemberId} onChange={e => update('referredByMemberId', e.target.value)}>
                          <MenuItem value="">— Select —</MenuItem>
                          {memberList.map(m => <MenuItem key={m.id} value={m.id}>{m.firstName} {m.lastName}</MenuItem>)}
                        </TextField>
                      </Grid>
                    )}
                    {form.referredBy === 'Staff Member' && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField fullWidth select label="Which Staff Member?" value={form.referredByStaffId} onChange={e => update('referredByStaffId', e.target.value)}>
                          <MenuItem value="">— Select —</MenuItem>
                          {staffList.map(s => <MenuItem key={s.id} value={s.id}>{s.firstName} {s.lastName}</MenuItem>)}
                        </TextField>
                      </Grid>
                    )}
                  </>
                ) : (
                  <>
                    <InfoRow label="First Name" value={form.firstName} />
                    <InfoRow label="Last Name" value={form.lastName} />
                    <InfoRow label="Email" value={user?.email} />
                    <InfoRow label="Phone" value={form.phone} />
                    <InfoRow label="Relationship" value={form.relationship} />
                    <InfoRow label="Nickname" value={form.nickname} />
                    <InfoRow label="Address" value={form.address ? `${form.address}, ${form.city}, ${form.state} ${form.zip}` : null} />
                    <InfoRow label="Referred By" value={form.referredBy} />
                  </>
                )}
              </Grid>

              {/* P/G 2 checkbox */}
              {editing && (
                <FormControlLabel
                  control={<Checkbox checked={hasP2} onChange={e => setHasP2(e.target.checked)} />}
                  label={<Typography sx={{ fontWeight: 600 }}>Add Parent / Guardian 2</Typography>}
                  sx={{ mb: 1 }}
                />
              )}

              {/* Parent / Guardian 2 */}
              {(hasP2 || form.guardian2FirstName || form.guardian2Email) && (
                <>
                  <SecTitle title="Parent / Guardian 2" />
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    {editing ? (
                      <>
                        <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth label="First Name" value={form.guardian2FirstName} onChange={e => update('guardian2FirstName', e.target.value)} /></Grid>
                        <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth label="Last Name" value={form.guardian2LastName} onChange={e => update('guardian2LastName', e.target.value)} /></Grid>
                        <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth label="Email" value={form.guardian2Email} onChange={e => update('guardian2Email', e.target.value)} helperText="Used to auto-link when they sign in" /></Grid>
                        <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth label="Phone" value={form.guardian2Phone} onChange={e => update('guardian2Phone', e.target.value)} /></Grid>
                      </>
                    ) : (
                      <>
                        <InfoRow label="First Name" value={form.guardian2FirstName} />
                        <InfoRow label="Last Name" value={form.guardian2LastName} />
                        <InfoRow label="Email" value={form.guardian2Email} />
                        <InfoRow label="Phone" value={form.guardian2Phone} />
                      </>
                    )}
                  </Grid>
                </>
              )}

              {/* Junior Athletes */}
              {linkedAthletes.map(junior => (
                <React.Fragment key={junior.id}>
                  <SecTitle title={`Junior: ${junior.firstName} ${junior.lastName}`} />
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    {editing ? (
                      <>
                        <Grid size={{ xs: 12, sm: 6 }}><TextField required fullWidth label="First Name *" value={juniorForms[junior.id]?.firstName || ''} onChange={e => updateJunior(junior.id, 'firstName', e.target.value)} /></Grid>
                        <Grid size={{ xs: 12, sm: 6 }}><TextField required fullWidth label="Last Name *" value={juniorForms[junior.id]?.lastName || ''} onChange={e => updateJunior(junior.id, 'lastName', e.target.value)} /></Grid>
                        <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth label="Phone" helperText="For class updates only" value={juniorForms[junior.id]?.phone || ''} onChange={e => updateJunior(junior.id, 'phone', e.target.value)} /></Grid>
                        <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth label="Nickname" value={juniorForms[junior.id]?.nickname || ''} onChange={e => updateJunior(junior.id, 'nickname', e.target.value)} /></Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <FormControl component="fieldset">
                            <FormLabel component="legend" sx={{ fontSize: '0.85rem' }}>Gender *</FormLabel>
                            <RadioGroup row value={juniorForms[junior.id]?.gender || ''} onChange={e => updateJunior(junior.id, 'gender', e.target.value)}>
                              <FormControlLabel value="Male" control={<Radio size="small" />} label="Male" />
                              <FormControlLabel value="Female" control={<Radio size="small" />} label="Female" />
                              <FormControlLabel value="Not Disclosed" control={<Radio size="small" />} label="Not Disclosed" />
                            </RadioGroup>
                          </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField fullWidth select label="Beach Experience / Class Level"
                            value={juniorForms[junior.id]?.beachExperience || ''}
                            onChange={e => {
                              const val = e.target.value;
                              const sg = val.toLowerCase().includes('no experience') || val.toLowerCase().includes('beginner') ? 'Beginner'
                                : val.toLowerCase().includes('intermediate') ? 'Intermediate'
                                : val.toLowerCase().includes('advanced') ? 'Advanced' : '';
                              setJuniorForms(f => ({ ...f, [junior.id]: { ...f[junior.id], beachExperience: val, skillGroup: sg } }));
                            }}
                            sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}>
                            {['No experience / Beginner', 'Intermediate', 'Advanced'].map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
                          </TextField>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField fullWidth select label="Shirt Size"
                            value={juniorForms[junior.id]?.shirtSize || ''}
                            onChange={e => setJuniorForms(f => ({ ...f, [junior.id]: { ...f[junior.id], shirtSize: e.target.value } }))}
                            sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}>
                            {['YXS', 'YS', 'YM', 'YL', 'YXL', 'S', 'M', 'L', 'XL', 'XXL'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                          </TextField>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField fullWidth label="Graduation Year" type="number"
                            value={juniorForms[junior.id]?.graduationYear || ''}
                            onChange={e => setJuniorForms(f => ({ ...f, [junior.id]: { ...f[junior.id], graduationYear: e.target.value } }))}
                            sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
                            inputProps={{ min: 2024, max: 2040 }} />
                        </Grid>
                      </>
                    ) : (
                      <>
                        <InfoRow label="First Name" value={junior.firstName} />
                        <InfoRow label="Last Name" value={junior.lastName} />

                        <InfoRow label="Phone" value={junior.phone || junior.athletePhone} />
                        <InfoRow label="Nickname" value={junior.nickname || junior.athleteNickname} />
                        <InfoRow label="Gender" value={junior.gender || junior.athleteGender} />
                        <InfoRow label="Beach Experience" value={junior.beachExperience} />
                        <InfoRow label="Skill Group" value={junior.skillGroup} />
                        <InfoRow label="Shirt Size" value={junior.shirtSize} />
                        <InfoRow label="Graduation Year" value={junior.graduationYear} />
                      </>
                    )}
                  </Grid>
                </React.Fragment>
              ))}

              {/* Registration Info */}
              <SecTitle title="Registration Info" />
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <InfoRow label="Member Since" value={memberSince ? formatTs(memberSince) : '—'} />
                <InfoRow label="Registration Status" value={userData?.registrationComplete ? 'Complete' : 'Incomplete'} />
              </Grid>

              {/* Security */}
              <SecTitle title="Security" />
              <Typography variant="body2" sx={{ color: '#888', mb: 1 }}>A password reset link will be sent to {user?.email}.</Typography>
              <Button variant="outlined" startIcon={<LockResetIcon />} onClick={handlePasswordReset} sx={{ borderColor: '#1E3A5F', color: '#1E3A5F' }}>
                Send Password Reset Email
              </Button>

            </TabPanel>

            {/* ── TAB 1: Athlete Details ── */}
            {athleteData && (
              <TabPanel value={tab} index={1}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 2 }}>Athlete Details</Typography>
                {editing ? (
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField fullWidth select label="Skill Group" value={athleteForm.skillGroup} onChange={e => setAthleteForm(p => ({ ...p, skillGroup: e.target.value }))}>
                        {['', 'Beginner', 'Intermediate', 'Advanced', 'Elite'].map(s => <MenuItem key={s} value={s}>{s || 'Not assigned'}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField fullWidth select label="Shirt Size" value={athleteForm.shirtSize} onChange={e => setAthleteForm(p => ({ ...p, shirtSize: e.target.value }))}>
                        {['YS','YM','YL','XS','S','M','L','XL','XXL'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth label="Indoor Experience" value={athleteForm.indoorExperience} onChange={e => setAthleteForm(p => ({ ...p, indoorExperience: e.target.value }))} /></Grid>
                    <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth label="Beach Experience" value={athleteForm.beachExperience} onChange={e => setAthleteForm(p => ({ ...p, beachExperience: e.target.value }))} /></Grid>
                  </Grid>
                ) : (
                  <Grid container spacing={2}>
                    <InfoRow label="Athlete Type" value={athleteData.athleteType ? athleteData.athleteType.charAt(0).toUpperCase() + athleteData.athleteType.slice(1) : '—'} />
                    <InfoRow label="Skill Group" value={athleteData.skillGroup} />
                    <InfoRow label="Shirt Size" value={athleteData.shirtSize} />
                    <InfoRow label="Indoor Experience" value={athleteData.indoorExperience} />
                    <InfoRow label="Beach Experience" value={athleteData.beachExperience} />
                  </Grid>
                )}
              </TabPanel>
            )}

            {/* ── TAB 2: Session History ── */}
            {athleteData && (
              <TabPanel value={tab} index={2}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 2 }}>Sessions</Typography>
                {attendance.length === 0 ? (
                  <Typography variant="body2" sx={{ color: '#aaa' }}>No session history found.</Typography>
                ) : attendance.map((a, i) => (
                  <Box key={a.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, px: 2, borderRadius: 2, backgroundColor: i % 2 === 0 ? '#f9f9f9' : 'white', mb: 0.5 }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatDate(a.date)}</Typography>
                      <Typography variant="caption" sx={{ color: '#888', textTransform: 'capitalize' }}>{a.sessionType || '—'}</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="caption" sx={{ color: '#888' }}>Balance after</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#1E3A5F' }}>{a.balanceAfter ?? '—'}</Typography>
                    </Box>
                  </Box>
                ))}
              </TabPanel>
            )}

            {/* ── TAB 3: Purchase History ── */}
            {showPurchasesTab && (
              <TabPanel value={tab} index={purchasesTabIndex}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 2 }}>Purchases</Typography>
                {purchases.length === 0 ? (
                  <Typography variant="body2" sx={{ color: '#aaa' }}>No purchases found.</Typography>
                ) : purchases.map((p, i) => {
                  // Three purchase shapes share this collection:
                  //   session plans → planName, sessionsAdded
                  //   day pass      → purchaseType === 'day_pass', has expiresAt
                  //   memberships   → tierName, purchaseType ('membership_one_time' | 'membership_start')
                  const isDayPass = p.purchaseType === 'day_pass';
                  const isMembership = !isDayPass && (
                    p.source === 'membership'
                    || p.purchaseType === 'membership_one_time'
                    || p.purchaseType === 'membership_start'
                  );

                  let title;
                  if (isDayPass) title = 'Day Pass';
                  else if (isMembership) title = p.tierName || 'Membership';
                  else title = p.planName || '—';

                  let subtitle;
                  if (isDayPass) {
                    const exp = p.expiresAt?.toDate?.() || (p.expiresAt?.seconds ? new Date(p.expiresAt.seconds * 1000) : null);
                    const expPast = exp && exp.getTime() < Date.now();
                    subtitle = exp
                      ? `${p.tierName || 'Walk-in'} · ${expPast ? 'Expired' : 'Valid until'} ${exp.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`
                      : (p.tierName || 'Walk-in');
                  } else if (isMembership) {
                    subtitle = p.purchaseType === 'membership_start' ? 'Subscription started' : 'One-time membership';
                  } else {
                    subtitle = p.sessionsAdded != null ? `${p.sessionsAdded} sessions added` : '';
                  }

                  return (
                    <Box key={p.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, px: 2, borderRadius: 2, backgroundColor: i % 2 === 0 ? '#f9f9f9' : 'white', mb: 0.5, gap: 1 }}>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{title}</Typography>
                        <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>{formatTs(p.purchaseDate)}</Typography>
                        {subtitle && <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>{subtitle}</Typography>}
                      </Box>
                      <Box sx={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#27AE60' }}>${(p.amountPaid || 0).toFixed(2)}</Typography>
                        {p.hostedInvoiceUrl && (
                          <Button
                            size="small"
                            variant="text"
                            href={p.hostedInvoiceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ fontSize: '0.7rem', textTransform: 'none', minWidth: 0, py: 0, color: '#1E3A5F' }}
                          >
                            Receipt
                          </Button>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </TabPanel>
            )}

            {/* ── Registrations tab ── */}
            <TabPanel value={tab} index={registrationsTabIndex}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 2 }}>Tournament Registrations</Typography>
              {registrations.length === 0 ? (
                <Typography variant="body2" sx={{ color: '#aaa' }}>No tournament registrations found.</Typography>
              ) : registrations.map((r, i) => {
                const statusColor = r.status === 'cancelled' ? '#E74C3C'
                  : r.payment_status === 'paid' ? '#27AE60'
                  : r.payment_status === 'partial' ? '#E67E22'
                  : '#888';
                const statusLabel = r.status === 'cancelled' ? 'Cancelled'
                  : r.payment_status === 'paid' ? 'Paid'
                  : r.payment_status === 'partial' ? 'Partial'
                  : r.payment_status === 'unpaid' ? 'Unpaid'
                  : (r.payment_status || r.status || '—');
                return (
                  <Box key={r.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, px: 2, borderRadius: 2, backgroundColor: i % 2 === 0 ? '#f9f9f9' : 'white', mb: 0.5 }}>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.tournamentName}</Typography>
                      <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>
                        {r.division || '—'}{r.partner_name ? ` · with ${r.partner_name}` : ''}
                      </Typography>
                      {r.tournamentDate && (
                        <Typography variant="caption" sx={{ color: '#888' }}>{formatDate(r.tournamentDate)}</Typography>
                      )}
                    </Box>
                    <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                      <Chip label={statusLabel} size="small"
                            sx={{ backgroundColor: statusColor, color: 'white', fontWeight: 600, fontSize: '0.7rem' }} />
                      {typeof r.amount_paid === 'number' && r.amount_paid > 0 && (
                        <Typography variant="caption" sx={{ display: 'block', color: '#888', mt: 0.5 }}>
                          ${(r.amount_paid / 100).toFixed(2)} paid
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </TabPanel>

            {/* ── Membership (freeze / cancel self-service) ── */}
            {hasMembership && (
              <TabPanel value={tab} index={membershipTabIndex}>
                {(() => {
                  const mem = userData.membership || {};
                  // Freeze and cancel act on a Stripe subscription, and
                  // manageMembership refuses outright without one
                  // (functions/index.js: 'No active membership subscription
                  // found on your account'). So a club-managed membership gets
                  // the details and no buttons, rather than buttons that
                  // always fail. Same shape as AthleteProfile's isStripeManaged.
                  const stripeManaged = !!mem.stripeSubscriptionId
                    || (mem.source === 'stripe' && !!mem.subscriptionId);
                  const isAnnualLike = /annual|year/i.test(mem.tierName || '');
                  const freezeActive = !!mem.freeze;
                  const freezeStarted = freezeActive && (() => {
                    const s = mem.freeze.startsAt?.toDate ? mem.freeze.startsAt.toDate() : new Date(mem.freeze.startsAt);
                    return s <= new Date();
                  })();
                  const fmtD = (v) => {
                    if (!v) return '\u2014';
                    const d = v.toDate ? v.toDate() : new Date(v);
                    return isNaN(d) ? '\u2014' : d.toLocaleDateString();
                  };
                  const yearNow = new Date().getFullYear();
                  const freezesUsed = (mem.freezeHistory || []).filter(h => {
                    const t = h.requestedAt?.toDate ? h.requestedAt.toDate() : new Date(h.requestedAt);
                    return t.getFullYear() === yearNow;
                  }).length;
                  // A club-managed membership carries its end date in
                  // expiresAt and nothing sweeps status when that date passes,
                  // so several day passes still read 'active' months later.
                  // Harmless while this tab was hidden from them; now that it
                  // is not, trust the date over the field rather than tell
                  // somebody their lapsed pass is Active. Stripe-managed rows
                  // are left alone — there, status is the live truth.
                  const clubExpired = !stripeManaged && !!mem.expiresAt
                    && (mem.expiresAt.toDate ? mem.expiresAt.toDate() : new Date(mem.expiresAt)) <= new Date();
                  const statusChip = clubExpired
                    ? { label: `Expired ${fmtD(mem.expiresAt)}`, bg: '#7F8C8D' }
                    : mem.cancelAtPeriodEnd
                    ? { label: `Cancels ${fmtD(mem.cancelAt || mem.currentPeriodEnd)}`, bg: '#E67E22' }
                    : mem.status === 'frozen'
                    ? { label: `Frozen until ${fmtD(mem.freeze?.endsAt)}`, bg: '#5D6D7E' }
                    : mem.status === 'trialing'
                    ? { label: 'Active (prepaid period)', bg: '#2E86C1' }
                    : mem.status === 'active'
                    ? { label: 'Active', bg: '#27AE60' }
                    : { label: mem.status || 'Unknown', bg: '#C0392B' };
                  return (
                    <>
                      {mmMsg && <Alert severity={mmMsg.severity} onClose={() => setMmMsg(null)} sx={{ mb: 2 }}>{mmMsg.text}</Alert>}
                      {/* Court time owed. Shown before the membership itself,
                          because until it is paid no new court can be booked. */}
                      {courtInvoices.map(inv => (
                        <Alert key={inv.id} severity="warning" sx={{ mb: 2 }}
                          action={
                            <Button color="inherit" size="small" disabled={!!payingInvoice}
                              onClick={() => payCourtInvoice(inv)}>
                              {payingInvoice === inv.id ? 'Opening…' : `Pay $${(Number(inv.amount) || 0).toFixed(2)}`}
                            </Button>
                          }>
                          {inv.description || 'Court time'} — ${(Number(inv.amount) || 0).toFixed(2)} outstanding
                          {inv.invoiceDate ? ` from ${inv.invoiceDate}` : ''}. New bookings are on hold until it is paid.
                        </Alert>
                      ))}
                      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F' }}>{mem.tierName || 'Membership'}</Typography>
                          <Chip size="small" label={statusChip.label} sx={{ backgroundColor: statusChip.bg, color: 'white', fontWeight: 700 }} />
                        </Box>
                        <Typography variant="body2" sx={{ color: '#555' }}>
                          {!stripeManaged
                            ? (mem.expiresAt
                                ? `Access through ${fmtD(mem.expiresAt)}.`
                                : 'Managed by the club.')
                            : mem.cancelAtPeriodEnd
                            ? `Access continues until ${fmtD(mem.cancelAt || mem.currentPeriodEnd)}.`
                            : `Next billing date: ${fmtD(mem.currentPeriodEnd)}`}
                        </Typography>
                        {freezeActive && !freezeStarted && (
                          <Typography variant="body2" sx={{ color: '#B9770E', mt: 0.5 }}>
                            Freeze scheduled: {fmtD(mem.freeze.startsAt)} → {fmtD(mem.freeze.endsAt)}
                          </Typography>
                        )}
                        {stripeManaged && !isAnnualLike && (
                          <Typography variant="caption" sx={{ color: '#888', display: 'block', mt: 0.5 }}>
                            Freezes used this year: {freezesUsed} of 2
                          </Typography>
                        )}
                      </Paper>

                      {!stripeManaged && (
                        <Typography variant="body2" sx={{ color: '#888' }}>
                          This membership is managed by the club rather than by a
                          card subscription. Contact us to change or end it.
                        </Typography>
                      )}

                      {stripeManaged && (
                        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                        {mem.cancelAtPeriodEnd ? (
                          <Button variant="contained" disabled={mmBusy} onClick={() => manageMembership('uncancel')}
                            sx={{ backgroundColor: '#27AE60' }}>
                            {mmBusy ? 'Working\u2026' : 'Undo Cancellation'}
                          </Button>
                        ) : (
                          <>
                            {freezeActive ? (
                              <Button variant="outlined" disabled={mmBusy} onClick={() => manageMembership('unfreeze')}>
                                {mmBusy ? 'Working\u2026' : (freezeStarted ? 'End Freeze Now' : 'Remove Scheduled Freeze')}
                              </Button>
                            ) : isAnnualLike ? (
                              <Tooltip title="Annual memberships can be paused by contacting us directly.">
                                <span><Button variant="outlined" disabled>Freeze (contact us)</Button></span>
                              </Tooltip>
                            ) : (
                              <Button variant="outlined" disabled={mmBusy || freezesUsed >= 2} onClick={() => { setFreezeMonths(1); setFreezeDialog(true); }}>
                                Freeze Membership
                              </Button>
                            )}
                            <Button variant="outlined" color="error" disabled={mmBusy || freezeActive} onClick={() => setCancelDialog(true)}>
                              Cancel Membership
                            </Button>
                          </>
                        )}
                        </Box>
                      )}

                      {/* Freeze dialog */}
                      <Dialog open={freezeDialog} onClose={() => setFreezeDialog(false)} fullWidth maxWidth="xs">
                        <DialogTitle>Freeze Membership</DialogTitle>
                        <DialogContent>
                          <Typography variant="body2" sx={{ mb: 2 }}>
                            Your freeze starts on your next billing date ({fmtD(mem.currentPeriodEnd)}). Billing, court reservations, and facility access pause during the freeze and resume automatically. You have {2 - freezesUsed} freeze{2 - freezesUsed === 1 ? '' : 's'} left this year.
                          </Typography>
                          <FormControl>
                            <FormLabel>Freeze duration</FormLabel>
                            <RadioGroup value={freezeMonths} onChange={e => setFreezeMonths(parseInt(e.target.value, 10))}>
                              {[1, 2, 3].map(m => <FormControlLabel key={m} value={m} control={<Radio />} label={`${m} month${m > 1 ? 's' : ''}`} />)}
                            </RadioGroup>
                          </FormControl>
                        </DialogContent>
                        <DialogActions>
                          <Button onClick={() => setFreezeDialog(false)}>Back</Button>
                          <Button variant="contained" disabled={mmBusy} onClick={() => manageMembership('freeze', freezeMonths)}>
                            {mmBusy ? 'Scheduling\u2026' : `Freeze for ${freezeMonths} month${freezeMonths > 1 ? 's' : ''}`}
                          </Button>
                        </DialogActions>
                      </Dialog>

                      {/* Cancel dialog */}
                      <Dialog open={cancelDialog} onClose={() => setCancelDialog(false)} fullWidth maxWidth="xs">
                        <DialogTitle>Cancel Membership?</DialogTitle>
                        <DialogContent>
                          <Typography variant="body2">
                            Your membership stays fully active until {fmtD(mem.currentPeriodEnd)} — you won’t be billed again after that. You can undo this any time before then.
                          </Typography>
                        </DialogContent>
                        <DialogActions>
                          <Button onClick={() => setCancelDialog(false)}>Keep Membership</Button>
                          <Button variant="contained" color="error" disabled={mmBusy} onClick={() => manageMembership('cancel')}>
                            {mmBusy ? 'Working\u2026' : 'Cancel Membership'}
                          </Button>
                        </DialogActions>
                      </Dialog>
                    </>
                  );
                })()}
              </TabPanel>
            )}

            {/* ── My Work (contractors) ── */}
            {isContractor && (
              <TabPanel value={tab} index={myWorkTabIndex}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 2 }}>
                  {workEditId ? 'Edit Work Entry' : 'Log Work Performed'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                  <TextField label="Date" type="date" size="small" value={workForm.date} sx={{ width: 165 }}
                    onChange={e => setWorkForm(f => ({ ...f, date: e.target.value }))} InputLabelProps={{ shrink: true }} />
                  <TextField label={(userData?.payType || 'per_job') === 'per_job' ? 'Hours (optional)' : 'Hours'} type="number" size="small" value={workForm.hours} sx={{ width: 130 }}
                    onChange={e => setWorkForm(f => ({ ...f, hours: e.target.value }))} />
                  {(userData?.payType) === 'self_rate' && (
                    <TextField label="Rate ($/hr)" type="number" size="small" value={workForm.rate} sx={{ width: 120 }}
                      onChange={e => setWorkForm(f => ({ ...f, rate: e.target.value }))} />
                  )}
                  <TextField label="Description of work" size="small" value={workForm.description} sx={{ flex: '1 1 260px' }}
                    multiline maxRows={3} onChange={e => setWorkForm(f => ({ ...f, description: e.target.value }))} />
                  <Button variant="contained" disabled={workSaving} onClick={submitWork}
                    sx={{ backgroundColor: '#1E3A5F', fontWeight: 700 }}>
                    {workSaving ? '…' : workEditId ? 'Update' : 'Submit'}
                  </Button>
                  {workEditId && (
                    <Button variant="text" onClick={() => { setWorkEditId(null); setWorkForm({ date: (() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}`; })(), description: '', hours: '', rate: '' }); }}>
                      Cancel
                    </Button>
                  )}
                </Box>
                {workMsg && (
                  <Typography variant="body2" sx={{ mb: 2, color: workMsg.startsWith('err:') ? '#E74C3C' : '#27AE60' }}>
                    {workMsg.slice(workMsg.indexOf(':') + 1)}
                  </Typography>
                )}
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 560, '& td, & th': { whiteSpace: 'nowrap' } }}>
                    <TableHead>
                      <TableRow>
                        {['Date', 'Description', 'Hours', 'Status', ''].map((h, hi) => (
                          <TableCell key={hi} sx={{ fontWeight: 700, color: '#1E3A5F' }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {workEntries.length === 0 ? (
                        <TableRow><TableCell colSpan={5} align="center" sx={{ color: '#aaa', py: 3 }}>No work entries yet.</TableCell></TableRow>
                      ) : workEntries.map(en => {
                        const locked = en.paid || (en.amount != null && en.approved !== false);
                        return (
                          <TableRow key={en.id}>
                            <TableCell>{en.date}</TableCell>
                            <TableCell sx={{ whiteSpace: 'normal' }}>{en.description}</TableCell>
                            <TableCell>{en.hours ?? '—'}</TableCell>
                            <TableCell>
                              {en.paid ? <span style={{ color: '#27AE60', fontWeight: 600 }}>${Number(en.amount || 0).toFixed(2)} - Paid</span>
                                : en.amount != null && en.approved !== false ? <span style={{ color: '#C9A84C', fontWeight: 600 }}>${Number(en.amount).toFixed(2)} - Approved</span>
                                : en.amount != null ? <span style={{ color: '#8A6D0B', fontWeight: 600 }}>${Number(en.amount).toFixed(2)} - Awaiting approval</span>
                                : <span style={{ color: '#999' }}>Pending review</span>}
                            </TableCell>
                            <TableCell align="right">
                              {!locked && (
                                <>
                                  <Button size="small" onClick={() => { setWorkEditId(en.id); setWorkForm({ date: en.date, description: en.description, hours: en.hours != null ? String(en.hours) : '', rate: en.rate != null ? String(en.rate) : '' }); }}>Edit</Button>
                                  <Button size="small" sx={{ color: '#E74C3C' }} onClick={() => deleteWork(en.id)}>Delete</Button>
                                </>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </TabPanel>
            )}

          </Box>
        </Paper>

        {/* Save / Cancel — bottom of page */}
        {editing && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2, mb: 2 }}>
            <Button onClick={() => setEditing(false)} variant="outlined" sx={{ borderColor: '#888', color: '#888', px: 4 }}>
              Cancel
            </Button>
            <Button startIcon={<SaveIcon />} onClick={handleSave} disabled={saving} variant="contained"
              sx={{ backgroundColor: '#27AE60', '&:hover': { backgroundColor: '#1E8449' }, px: 4 }}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        )}
      </Box>
    </Layout>
  );
}

export default Profile;
